import React, { useState, useEffect, useCallback } from 'react';
import { generateEmail, analyzeStory } from './services/api';
import { EMAIL_STYLES, OfferSnapshot as OfferSnapshotType, NewsletterTopics as NewsletterTopicsType } from './types';
import StoryInput from './components/StoryInput';
import StyleSelector from './components/StyleSelector';
import ToneSelector from './components/ToneSelector';
import OptionalFields from './components/OptionalFields';
import OfferSnapshot from './components/OfferSnapshot';
import NewsletterTopics from './components/NewsletterTopics';
import EmailOutput from './components/EmailOutput';
import ErrorBoundary from './components/ErrorBoundary';
import { Sparkles, AlertCircle, Save } from 'lucide-react';

// LocalStorage key for auto-save
const STORAGE_KEY = 'creator-email-engine-draft';
const AUTO_SAVE_INTERVAL = 10000; // 10 seconds

// Load saved draft from localStorage
const loadSavedDraft = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      // Check if draft is less than 24 hours old
      if (parsed.timestamp && Date.now() - parsed.timestamp < 24 * 60 * 60 * 1000) {
        return parsed.formData;
      }
    }
  } catch (e) {
    console.error('Error loading saved draft:', e);
  }
  return null;
};

// Default form state
const getDefaultFormData = () => ({
  story: '',
  selectedStyle: EMAIL_STYLES[0],
  tone1: '',
  tone2: '',
  pastNewsletterSample: '',
  offerSnapshot: OfferSnapshotType,
  newsletterTopics: NewsletterTopicsType
});

function App() {
  // Initialize form data from saved draft or defaults
  const [formData, setFormData] = useState(() => {
    const saved = loadSavedDraft();
    return saved || getDefaultFormData();
  });

  const [hasSavedDraft, setHasSavedDraft] = useState(() => !!loadSavedDraft());
  const [lastSaved, setLastSaved] = useState(null);

  const [generatedEmail, setGeneratedEmail] = useState({
    subject: '',
    subjectLines: {
      fomo: '',
      curiosity: '',
      value: '',
      question: '',
      special: ''
    },
    hook: '',
    body: '',
    cta: ''
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [feedbackState, setFeedbackState] = useState({
    isAnalyzing: false,
    isExpanding: false,
    suggestions: [],
    selectedSuggestion: null,
    showFeedbackPanel: false
  });

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Auto-save form data to localStorage
  useEffect(() => {
    // Only save if there's actual content
    if (formData.story.trim() || formData.pastNewsletterSample.trim()) {
      const saveTimer = setTimeout(() => {
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify({
            formData,
            timestamp: Date.now()
          }));
          setLastSaved(new Date());
          setHasSavedDraft(true);
        } catch (e) {
          console.error('Error saving draft:', e);
        }
      }, AUTO_SAVE_INTERVAL);

      return () => clearTimeout(saveTimer);
    }
  }, [formData]);

  // Clear draft after successful generation
  const clearSavedDraft = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      setHasSavedDraft(false);
      setLastSaved(null);
    } catch (e) {
      console.error('Error clearing draft:', e);
    }
  }, []);

  // Analyze story and get expansion suggestions
  const handleAnalyzeStory = async () => {
    setFeedbackState(prev => ({ ...prev, isAnalyzing: true, showFeedbackPanel: true }));
    setError('');

    try {
      const response = await analyzeStory({
        story: formData.story,
        selectedStyle: formData.selectedStyle,
        offerSnapshot: formData.offerSnapshot,
        newsletterTopics: formData.newsletterTopics,
        generatedEmail: generatedEmail
      });

      setFeedbackState(prev => ({
        ...prev,
        isAnalyzing: false,
        suggestions: response.suggestions || [],
        showFeedbackPanel: true
      }));
    } catch (err) {
      setError('Failed to analyze story: ' + err.message);
      setFeedbackState(prev => ({
        ...prev,
        isAnalyzing: false,
        showFeedbackPanel: false
      }));
    }
  };

  // Handle clicking a suggestion - make targeted improvement to existing email
  const handleSuggestionClick = async (suggestion) => {
    setFeedbackState(prev => ({
      ...prev,
      isExpanding: true,
      selectedSuggestion: suggestion
    }));
    setError('');

    try {
      // Import the improve function
      const { improveEmail } = await import('./services/api');

      // Call the improve endpoint with the original email and suggestion
      const response = await improveEmail({
        story: formData.story,
        selectedStyle: formData.selectedStyle,
        tone1: formData.tone1,
        tone2: formData.tone2,
        offerSnapshot: formData.offerSnapshot,
        newsletterTopics: formData.newsletterTopics,
        originalEmail: generatedEmail,
        suggestion: suggestion
      });

      setGeneratedEmail(response);
      setFeedbackState({
        isAnalyzing: false,
        isExpanding: false,
        suggestions: [],
        selectedSuggestion: null,
        showFeedbackPanel: false
      });
    } catch (err) {
      setError('Failed to improve email: ' + err.message);
      setFeedbackState(prev => ({
        ...prev,
        isExpanding: false,
        selectedSuggestion: null
      }));
    }
  };

  // Cancel feedback and hide suggestions
  const handleCancelFeedback = () => {
    setFeedbackState({
      isAnalyzing: false,
      isExpanding: false,
      suggestions: [],
      selectedSuggestion: null,
      showFeedbackPanel: false
    });
  };

  const handleGenerateEmail = async () => {
    if (!formData.story.trim()) {
      setError('Please enter your story first.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await generateEmail(formData);
      setGeneratedEmail(response);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="bg-white/90 backdrop-blur-lg shadow-xl border-b border-white/20 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-br from-primary-500 via-blue-500 to-secondary-500 rounded-2xl flex items-center justify-center shadow-lg">
                <Sparkles className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-primary-600 to-blue-600 bg-clip-text text-transparent">
                  Creator Email Engine
                </h1>
                <p className="text-sm text-gray-600 hidden sm:block">
                  Turn your stories into compelling emails that convert
                </p>
              </div>
            </div>
            <div className="hidden md:flex items-center space-x-2 bg-primary-50 px-4 py-2 rounded-full">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-sm font-medium text-primary-700">Ready to generate</span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Input Form */}
          <div className="space-y-8">
            <div className="card">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">
                Your Story & Settings
              </h2>
              
              <div className="space-y-6">
                <StoryInput
                  story={formData.story}
                  onStoryChange={(value) => updateFormData('story', value)}
                  selectedStyle={formData.selectedStyle}
                  onStyleChange={(value) => updateFormData('selectedStyle', value)}
                />

                <StyleSelector
                  selectedStyle={formData.selectedStyle}
                  onStyleChange={(value) => updateFormData('selectedStyle', value)}
                />

                <ToneSelector
                  tone1={formData.tone1}
                  tone2={formData.tone2}
                  onTone1Change={(value) => updateFormData('tone1', value)}
                  onTone2Change={(value) => updateFormData('tone2', value)}
                />

                <OptionalFields
                  pastNewsletterSample={formData.pastNewsletterSample}
                  onPastNewsletterSampleChange={(value) => updateFormData('pastNewsletterSample', value)}
                />

                <OfferSnapshot
                  offerSnapshot={formData.offerSnapshot}
                  onOfferChange={(value) => updateFormData('offerSnapshot', value)}
                />

                <NewsletterTopics
                  newsletterTopics={formData.newsletterTopics}
                  onTopicsChange={(value) => updateFormData('newsletterTopics', value)}
                />
              </div>

              {/* Draft Recovery Notice */}
              {hasSavedDraft && formData.story.trim() && (
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Save className="w-4 h-4 text-blue-600" />
                      <p className="text-sm text-blue-700">
                        {lastSaved
                          ? `Draft saved ${lastSaved.toLocaleTimeString()}`
                          : 'Draft recovered from previous session'}
                      </p>
                    </div>
                    <button
                      onClick={clearSavedDraft}
                      className="text-xs text-blue-600 hover:text-blue-800 underline"
                    >
                      Clear
                    </button>
                  </div>
                </div>
              )}

              {/* Error Message */}
              {error && (
                <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <AlertCircle className="w-5 h-5 text-red-600" />
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                </div>
              )}

              {/* Generate Button */}
              <div className="mt-8">
                <button
                  onClick={handleGenerateEmail}
                  disabled={isLoading || !formData.story.trim()}
                  className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-3 text-lg relative overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-primary-400 via-blue-400 to-primary-400 opacity-0 animate-pulse group-hover:opacity-20"></div>
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                      <span className="font-semibold">Crafting your email...</span>
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5" />
                      <span className="font-bold">✨ Generate Compelling Email ✨</span>
                    </>
                  )}
                </button>
                {!formData.story.trim() && (
                  <p className="mt-3 text-sm text-gray-500 text-center">
                    Enter your story above to get started
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Output */}
          <div className="lg:sticky lg:top-8 lg:h-fit">
            <ErrorBoundary fallbackMessage="There was an error displaying your email. Please try generating again.">
              <EmailOutput
                email={generatedEmail}
                isLoading={isLoading}
                offerSnapshot={formData.offerSnapshot}
                feedbackState={feedbackState}
                onImproveClick={handleAnalyzeStory}
                onSuggestionClick={handleSuggestionClick}
                onCancelFeedback={handleCancelFeedback}
              />
            </ErrorBoundary>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white/50 backdrop-blur-sm border-t border-white/20 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <div className="w-6 h-6 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-lg flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <span className="text-lg font-semibold bg-gradient-to-r from-primary-600 to-blue-600 bg-clip-text text-transparent">
                Creator Email Engine
              </span>
            </div>
            <p className="text-gray-600 max-w-md mx-auto">
              Transform your stories into compelling newsletter emails that engage your audience and drive results.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App; 